import type { CourseTreeNode } from "@/types/generated";
import type { TextStoryboard } from "@/types/storyboard";
import { Text } from "@/components/storyboard/text/Text";
import { TextFlashcard } from "@/components/storyboard/text/TextFlashcard";
import { TextChecklist } from "@/components/storyboard/text/TextChecklist";
import { TextQuestions } from "@/components/storyboard/text/TextQuestions";
import { TextLearningObjectiveQuestion } from "@/components/storyboard/text/TextLearningObjectiveQuestion";
import { TextLearningObjectiveAssertion } from "@/components/storyboard/text/TextLearningObjectiveAssertion";
import { QuizValidationService } from "@/services/QuizValidationService";
import WorksheetInteractiveFlow from "@/components/storyboard/worksheet/WorksheetInteractiveFlow";
import { TextSummary } from "@/components/storyboard/text/TextSummary";
import { TextAnalogy } from "@/components/storyboard/text/TextAnalogy";
import { TextHint } from "@/components/storyboard/text/TextHint";
import { TextProvocativeQuestion } from "@/components/storyboard/text/TextProvocativeQuestion";
import { TextExample } from "@/components/storyboard/text/TextExample";
import { TextProblemSolving } from "@/components/storyboard/text/TextProblemSolving";
import { TextSelfReflectionQuestions } from "@/components/storyboard/text/TextSelfReflectionQuestions";
import { TextExperienceReflection } from "@/components/storyboard/text/TextExperienceReflection";
import { TextStructured } from "@/components/storyboard/text/TextStructured";
import { TextDialogue } from "@/components/storyboard/text/TextDialogue";
import { StoryboardFallback } from "@/components/storyboard/StoryboardFallback";
import { storyboardTitle } from "@/lib/storyboardTitle";

interface Props {
  data: TextStoryboard;
  contentElement: CourseTreeNode;
}

export const StoryboardTextRenderer = ({ data, contentElement }: Props) => {
  switch (data.subType) {
    case "CHECKLIST":
      return <TextChecklist data={data.text} title={contentElement.title} />;
    case "FLASHCARDS":
      return (
        <TextFlashcard
          title={contentElement.title}
          contentElementId={contentElement.id}
          data={data.text}
        />
      );
    case "QUESTION":
      return <TextQuestions questions={data.text} />;
    case "LEARNING_OBJECTIVE_QUESTION":
      return <TextLearningObjectiveQuestion data={data.text} />;
    case "LEARNING_OBJECTIVE_ASSERTION":
      return (
        <TextLearningObjectiveAssertion
          data={data.text}
          title={contentElement.title}
        />
      );
    case "WORKSHEET":
      return (
        <WorksheetInteractiveFlow
          id={contentElement.id}
          title={storyboardTitle(data.text.title, contentElement.title)}
          data={data.text}
          onValidate={(answer, taskIndex) =>
            QuizValidationService.validateAnswer({
              type: "worksheet",
              userInput: answer,
              contentElementId: contentElement.id,
              entryIndex: taskIndex,
            })
          }
        />
      );
    case "TEXT":
      return <TextStructured data={data.text} />;
    case "SUMMARY":
      return <TextSummary data={data.text} />;
    case "ANALOGY":
      return <TextAnalogy text={data.text} />;
    case "HINT":
      return <TextHint data={data.text} title={contentElement.title} />;
    case "PROVOCATIVE_QUESTION":
      return <TextProvocativeQuestion text={data.text} />;
    case "EXAMPLE":
      return <TextExample data={data.text} />;
    case "EXAMPLE_PROBLEMSOLVING":
      return <TextProblemSolving data={data.text} />;
    case "SELF_REFLECTION_QUESTIONS":
      return (
        <TextSelfReflectionQuestions
          data={data.text}
          contentElementId={contentElement.id}
          title={contentElement.title}
        />
      );
    case "EXPERIENCE_REFLECTION":
      return (
        <TextExperienceReflection
          data={data.text}
          contentElementId={contentElement.id}
          title={contentElement.title}
        />
      );
    case "DIALOGUE":
      return <TextDialogue data={data.text} />;
    case "FALLBACK":
      return <Text text={data.text} />;
    default:
      return <StoryboardFallback title={contentElement.title} />;
  }
};
